/**
 * usePricing - Hook for fetching dynamic pricing from API
 *
 * Returns formatted price strings and raw values for use across all public pages.
 * Provides sensible fallback values if API is unavailable.
 *
 * Caching strategy:
 * 1. Try to fetch fresh pricing from API
 * 2. On success, cache to localStorage (valid for 24 hours)
 * 3. On failure, try localStorage cache
 * 4. If no cache, use hardcoded fallback values
 */

import { useState, useEffect } from "react";
import { subscriptionsApi } from "@/lib/api";

interface PricingData {
  // Raw values
  priceExclVat: number | null;
  priceInclVat: number | null;
  vatRate: number;
  isLoading: boolean;
  error: string | null;

  // Formatted strings (with fallbacks)
  formattedPriceExclVat: string; // "2 495"
  formattedPriceInclVat: string; // "3 119"
  formattedPriceWithSuffix: string; // "2 495 kr"
  formattedPriceExclVatFull: string; // "2 495 kr exkl. moms"
  formattedPriceInclVatFull: string; // "3 119 kr inkl. moms"
}

interface CachedPricing {
  priceExclVat: number;
  priceInclVat: number;
  vatRate: number;
  timestamp: number;
}

// Default fallback values (used when API and cache are unavailable)
const DEFAULT_PRICE_EXCL_VAT = 2495;
const DEFAULT_VAT_RATE = 0.25;

// Cache settings
const CACHE_KEY = "kvanten_pricing_cache";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function getCachedPricing(): CachedPricing | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: CachedPricing = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - data.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function setCachedPricing(priceExclVat: number, priceInclVat: number, vatRate: number): void {
  try {
    const data: CachedPricing = {
      priceExclVat,
      priceInclVat,
      vatRate,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore localStorage errors (quota exceeded, private browsing, etc.)
  }
}

export function usePricing(): PricingData {
  const [priceExclVat, setPriceExclVat] = useState<number | null>(null);
  const [priceInclVat, setPriceInclVat] = useState<number | null>(null);
  const [vatRate, setVatRate] = useState<number>(DEFAULT_VAT_RATE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchPricing = async () => {
      try {
        const res = await subscriptionsApi.getPrice();
        if (mounted && res.success && res.data) {
          setPriceExclVat(res.data.priceExclVat);
          setPriceInclVat(res.data.priceInclVat);
          if (res.data.vatRate !== undefined) {
            setVatRate(res.data.vatRate);
          }
          // Cache successful response
          setCachedPricing(
            res.data.priceExclVat,
            res.data.priceInclVat,
            res.data.vatRate ?? DEFAULT_VAT_RATE
          );
        }
      } catch {
        if (mounted) {
          setError("Kunde inte hämta pris");

          // Try to use cached values first
          const cached = getCachedPricing();
          if (cached) {
            setPriceExclVat(cached.priceExclVat);
            setPriceInclVat(cached.priceInclVat);
            setVatRate(cached.vatRate);
          } else {
            // Fall back to hardcoded defaults
            setPriceExclVat(DEFAULT_PRICE_EXCL_VAT);
            setPriceInclVat(Math.round(DEFAULT_PRICE_EXCL_VAT * (1 + DEFAULT_VAT_RATE)));
          }
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchPricing();

    return () => {
      mounted = false;
    };
  }, []);

  // Calculate formatted values with fallbacks
  const effectivePriceExcl = priceExclVat ?? DEFAULT_PRICE_EXCL_VAT;
  const effectivePriceIncl = priceInclVat ?? Math.round(DEFAULT_PRICE_EXCL_VAT * (1 + vatRate));

  const formatter = new Intl.NumberFormat("sv-SE");

  return {
    // Raw values
    priceExclVat,
    priceInclVat,
    vatRate,
    isLoading,
    error,

    // Formatted strings
    formattedPriceExclVat: formatter.format(effectivePriceExcl),
    formattedPriceInclVat: formatter.format(effectivePriceIncl),
    formattedPriceWithSuffix: `${formatter.format(effectivePriceExcl)} kr`,
    formattedPriceExclVatFull: `${formatter.format(effectivePriceExcl)} kr exkl. moms`,
    formattedPriceInclVatFull: `${formatter.format(effectivePriceIncl)} kr inkl. moms`,
  };
}
